/**
 * Which discipline answers, as the reader sees it.
 *
 * Two controls and one type, in a file of their own because they are one
 * subject appearing in two places: the toggle sits in the composer and decides
 * what the *next* question asks for, while the badge sits on an answer and
 * says what *that* one was produced under. Putting the toggle in App.tsx and
 * the badge in exchange.tsx would leave the labels for one idea in two files,
 * free to drift apart the first time the wording changes.
 *
 * The mode rides each ask rather than the conversation, so a transcript can
 * legitimately mix the two — which is exactly why the badge is per message.
 * A screenshot of a research answer must not read as corpus-grounded.
 */

import type { JSX } from 'react'

import { t } from './i18n'

/** Mirrors the server's enum. Anything else is not a mode. */
export type Mode = 'grounded' | 'research'

export const GROUNDED: Mode = 'grounded'
export const RESEARCH: Mode = 'research'

const CHOICES: readonly Mode[] = [GROUNDED, RESEARCH]

/** The grounded button's label for a reference grammar: "Bible only", "Law only". */
function groundedLabel(grammar: string | null): string {
  if (grammar === 'biblical') return t('mode.grounded.biblical')
  if (grammar === 'statute') return t('mode.grounded.statute')
  return t('mode.grounded')
}

/**
 * Choose the discipline for the next question.
 *
 * Rendered only where the corpus says research is available — the server
 * refuses the ask as well, so this is a drawing decision rather than the rule,
 * and a control the reader cannot use is a control the interface should not
 * offer.
 *
 * Two buttons rather than a checkbox: both states are named, so "grounded" is
 * something the reader can see they are in rather than the absence of
 * something they did not tick.
 */
export function ModeToggle({
  mode,
  grammar,
  onChange,
}: {
  mode: Mode
  /** The corpus's reference grammar, which names what a grounded answer is limited to. */
  grammar: string | null
  onChange: (mode: Mode) => void
}): JSX.Element {
  return (
    <div className="modes" role="group" aria-label={t('mode.legend')}>
      {CHOICES.map((choice) => (
        <button
          key={choice}
          type="button"
          className="modes__choice"
          aria-pressed={mode === choice}
          onClick={() => onChange(choice)}
          title={t(choice === RESEARCH ? 'mode.researchHint' : 'mode.groundedHint')}
        >
          {choice === RESEARCH ? t('mode.research') : groundedLabel(grammar)}
        </button>
      ))}
    </div>
  )
}

/**
 * What this answer was produced under.
 *
 * Nothing for a grounded answer: it is the default state and the whole
 * product, and a badge on every message would be the badge clutter the
 * implicit-prose rule exists to prevent. A research answer says so, because
 * the reader is owed the difference at a glance.
 */
export function ModeBadge({ mode }: { mode: Mode | undefined }): JSX.Element | null {
  if (mode !== RESEARCH) return null
  return (
    <p className="mode-badge">
      {t('mode.badge')} <span className="mode-badge__note">{t('mode.badgeNote')}</span>
    </p>
  )
}
